import { useTranslation } from "react-i18next";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /**
   * 0-based index of the first row on screen, for a caller whose offset is not
   * a multiple of `pageSize` — the notices review run continues from an offset
   * backed off by the letters it withdrew. Without it such a caller reads
   * "Showing 1 to 20" over rows 18-37 with Previous greyed out; with it the
   * range and both buttons are computed from the offset itself.
   */
  startIndex?: number;
  /** Offset-based stepping, paired with `startIndex`. */
  onOffsetChange?: (offset: number) => void;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  startIndex,
  onOffsetChange,
}: PaginationProps) {
  const { t } = useTranslation("common");
  // Page-number mode stays byte-identical for every caller that does not opt
  // in; an ALIGNED offset produces the same numbers through either arm.
  const byOffset = startIndex !== undefined;
  const start = totalItems === 0 ? 0 : byOffset ? startIndex + 1 : (currentPage - 1) * pageSize + 1;
  const end = byOffset
    ? Math.min(startIndex + pageSize, totalItems)
    : Math.min(currentPage * pageSize, totalItems);
  const atStart = byOffset ? startIndex <= 0 : currentPage <= 1;
  const atEnd = byOffset ? end >= totalItems : currentPage >= totalPages;
  const goPrevious = () =>
    byOffset && onOffsetChange
      ? onOffsetChange(Math.max(0, startIndex - pageSize))
      : onPageChange(currentPage - 1);
  const goNext = () =>
    byOffset && onOffsetChange
      ? onOffsetChange(startIndex + pageSize)
      : onPageChange(currentPage + 1);

  return (
    <div className="flex items-center justify-between px-1 py-3">
      <p className="text-sm text-muted">
        {t("pagination.showing", { from: start, to: end, total: totalItems })}
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={atStart}
          onClick={goPrevious}
          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          {t("previous")}
        </button>

        <span className="text-sm text-muted">
          {currentPage} / {totalPages || 1}
        </span>

        <button
          type="button"
          disabled={atEnd}
          onClick={goNext}
          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-on-surface transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          {t("next")}
        </button>
      </div>
    </div>
  );
}
